import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import {
  sortFilterOptions,
  timeFilterOptions,
} from '../../shared/models/ranking.model';
import { RankingService } from './../../shared/services/ranking.service';

@Component({
  selector: 'app-ranking-page',
  imports: [
    CommonModule,
    CardModule,
    RouterModule,
    TranslateModule,
    FormsModule,
    ToastModule,
    TableModule,
    ButtonModule,
    DatePickerModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TooltipModule,
    SelectModule,
    FloatLabelModule,
  ],
  providers: [MessageService, RankingService],
  templateUrl: './ranking-page.component.html',
  styleUrl: './ranking-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RankingPageComponent {
  @ViewChild('ranking') rankingTable!: Table;
  #messageService = inject(MessageService);
  rankingService = inject(RankingService);
  #router = inject(Router);
  #route = inject(ActivatedRoute);
  rankingmode = 'default';

  timeFilterOptions = timeFilterOptions;
  sortFilterOptions = sortFilterOptions;

  selectedTimeFilter = 'all';
  selectedSortFilter = 'points';
  selectedNickname?: string | null;

  get selectedTimeFilterLabel(): string {
    const foundOption = this.timeFilterOptions.find(
      (option) => option.value === this.selectedTimeFilter,
    );
    return foundOption?.text ?? '';
  }

  rangeDates: Date[] = [
    (() => {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      date.setHours(0, 0, 0, 0);
      return date;
    })(),
    (() => {
      const date = new Date();
      date.setHours(23, 59, 59, 999);
      return date;
    })(),
  ];

  constructor() {}

  ngOnInit(): void {
    this.rankingmode = this.#route.snapshot.data['mode'];
    this.selectedNickname = this.#route.snapshot.paramMap.get('nickname');
    this.setInitialFilters();
    this.getRanking();
  }

  selectDateRange() {
    if (this.rangeDates[0] && this.rangeDates[1]) {
      const time_from = new Date(this.rangeDates[0]);
      time_from.setHours(0, 0, 0, 0);

      const time_to = new Date(this.rangeDates[1]);
      time_to.setHours(23, 59, 59, 999);

      this.rankingService.getRanking({
        time_from,
        time_to,
      });
    }
  }

  applyFilter() {
    this.getRanking();
  }

  applyGlobalFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.rankingTable.filterGlobal(filterValue, 'contains');
  }

  getRanking() {
    switch (this.rankingmode) {
      case 'default':
        this.rankingService.getRanking({
          time_from: this.rangeDates[0],
          time_to: this.rangeDates[1],
        });

        break;
      case 'screen':
        this.rankingService.getUserRanking({
          time: this.selectedTimeFilter,
          sort: this.selectedSortFilter,
        });
        break;
      case 'user':
        this.rankingService.getUserRanking({
          time: this.selectedTimeFilter,
          sort: this.selectedSortFilter,
          nickname: this.selectedNickname,
        });
        break;
    }
  }
  setInitialFilters() {
    switch (this.rankingmode) {
      case 'default':
        this.selectedTimeFilter = 'all';

        break;
      case 'screen':
        this.selectedTimeFilter = 'today';
        break;
      case 'user':
        this.selectedTimeFilter = 'all';
        break;
    }
  }

  viewUserRanking(e: any) {
    this.selectedNickname = e.nickname;
    this.#router.navigate(['/ranking', 'user', this.selectedNickname]);
    this.getRanking();
  }
}
